import { action, computed, makeObservable, onBecomeObserved } from "mobx"

import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { transactionsTopupFilter } from "@src/pages/transactionsTopup/constants"
import { catchErrors, topupsApi } from "@src/shared/api"
import {
  NetworkCrypto,
  Pagination,
  SymbolCrypto,
  TopupPaymentResponse,
} from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"

export type TransactionsTopUpLoadRequest = {
  address?: string
  amlLevel: "high" | "low" | "medium"
  id?: string
  network?: NetworkCrypto
  order?: "asc" | "desc"
  page?: number
  status?: "confirmed" | "failed" | "pending"
  symbol?: SymbolCrypto
  topupId?: string
  txId?: string
}

export type TransactionsTopUpCollectionFilters = {
  order?: "asc" | "desc"
  status?: "failed" | "pending" | "success"
}

export type TopupPayment = Required<TopupPaymentResponse> & { exchange: null }

export class TransactionsTopUpCollection extends loading(
  ["fetch"],
  CollectionWithPagination<TopupPayment>,
) {
  private _currencies = GlobalCurrenciesCollection()

  constructor() {
    super()

    makeObservable(this, {
      fetch: action.bound,
      networks: computed,
      symbols: computed,
    })

    onBecomeObserved(this, "isLoading", this.load)
  }

  async fetch(
    req: TransactionsTopUpLoadRequest,
  ): Promise<{ data: TopupPayment[]; pagination: Pagination } | null> {
    const symbols = [req.symbol]
      .reduce((acc, i) => {
        return [
          ...acc,
          ...this._currencies.crypto
            .filter((c) => c.symbol_alias === i)
            .map((c) => c.symbol),
        ]
      }, [] as string[])
      .join(",")

    try {
      const {
        data: { data: { data, pagination } = {} },
      } = await topupsApi.getTopupPayments(
        req.order,
        this.calcOffset(req.page),
        this.pageSize,
        req.id,
        req.topupId,
        req.address,
        undefined,
        req.txId,
        req.network,
        req.symbol,
        req.status,
        req.amlLevel,
      )

      if (data && pagination) {
        return {
          data: data as TopupPayment[],
          pagination,
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }

    return null
  }

  get filter() {
    return new CollectionFilters<TransactionsTopUpCollectionFilters>(
      [
        ...transactionsTopupFilter,
        CollectionFilters.createField("network").select(this.networks, {
          col: 6,
          label: "Network",
          placeholder: "Select a network",
        }),

        CollectionFilters.createField("symbol").select(this.symbols, {
          col: 6,
          label: "Symbol",
          placeholder: "Select a symbol",
        }),
      ],
      this.load,
    )
  }

  get networks() {
    return this._currencies.cryptoNetworks.map((i) => ({
      label: i,
      value: i,
    }))
  }

  get symbols() {
    return [...new Set(this._currencies.crypto.map((i) => i.symbol))].map(
      (i) => ({ label: i, value: i }),
    )
    // return [
    //   ...new Set(
    //     this._currencies.crypto.map((i) => `${i.symbol}_${i.symbol_alias}`),
    //   ),
    // ].reduce((acc: Record<string, any>[], i) => {
    //   const [symbol, symbol_alias] = i.split("_")
    //   const alreadyHas = acc.some((i) => i.value === symbol_alias)
    //
    //   if (!alreadyHas) {
    //     acc.push({
    //       key: `${symbol}_${symbol_alias}`,
    //       label: symbol_alias,
    //       value: symbol_alias,
    //     })
    //   }
    //
    //   return acc
    // }, [])
  }
}
